import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DataContext } from '../Provider/dataProvider';
import { fetchWithAuth } from '../utils/authUtils';
import {
    formatMedicalDate,
    formatMedicalDateTime,
    formatRemindBefore,
} from '../utils/alertUtils';
import '../css/revisitAppointment.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const InfoRow = ({ icon, label, value, highlight = false }) => {
    if (!value || value === '—') return null;
    return (
        <div className={`revisit-info-row${highlight ? ' revisit-info-row--highlight' : ''}`}>
            <i className={`fa-solid ${icon} revisit-info-icon`} />
            <div>
                <div className="revisit-info-label">{label}</div>
                <div className="revisit-info-value">{value}</div>
            </div>
        </div>
    );
};

const RevisitAppointmentPage = () => {
    const { recordId: paramRecordId } = useParams();
    const [searchParams] = useSearchParams();
    const { data } = useContext(DataContext);
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const isElderly = data?.role === 'elderly';
    const homePath = isElderly ? '/home_elderly' : '/home_caregiver';
    const medicalPath = searchParams.get('elderlyId')
        ? `/medical-history/${searchParams.get('elderlyId')}`
        : isElderly
            ? `/medical-history/${data?.id || ''}`
            : '/medical-history';

    const qp = useMemo(() => ({
        time: searchParams.get('time') || '',
        place: searchParams.get('place') || '',
        note: searchParams.get('note') || '',
        doctorNotes: searchParams.get('doctorNotes') || '',
        visitDate: searchParams.get('visitDate') || '',
        remindBefore: searchParams.get('remindBefore') || '',
        elderlyName: searchParams.get('elderlyName') || '',
    }), [searchParams]);

    useEffect(() => {
        if (!paramRecordId) {
            setLoading(false);
            setError('Không có mã hồ sơ.');
            return;
        }

        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                setError('');
                const res = await fetchWithAuth(`${API_BASE}/medical-records/record/${paramRecordId}`);
                const json = await res.json().catch(() => ({}));
                if (!res.ok || !json?.success) {
                    throw new Error(json?.message || 'Không thể tải hồ sơ y tế.');
                }
                if (!aborted) setRecord(json.data || null);
            } catch (err) {
                if (!aborted) {
                    setRecord(null);
                    setError(err.message || 'Không thể tải hồ sơ y tế.');
                }
            } finally {
                if (!aborted) setLoading(false);
            }
        })();

        return () => { aborted = true; };
    }, [paramRecordId]);

    const revisitAt = record?.revisitAt || qp.time;
    const hospital = record?.hospitalName || qp.place;
    const diagnosis = record?.diagnosis || qp.note;
    const doctorNotes = record?.doctorNotes || qp.doctorNotes;
    const visitDate = record?.visitDate || qp.visitDate;
    const remindBefore = record?.revisitRemindBeforeMinutes ?? qp.remindBefore;
    const patientName = record?.fullName || qp.elderlyName || (isElderly ? 'Tôi' : 'Người thân');

    return (
        <div className="revisit-page">
            <div className="revisit-shell">
                <Link to={homePath} className="revisit-back">
                    <i className="fa-solid fa-arrow-left" /> Quay lại
                </Link>

                <div className="revisit-card">
                    <div className="revisit-card-header">
                        <i className="fa-solid fa-calendar-check revisit-title-icon" />
                        <h1>{isElderly ? 'Lịch tái khám của tôi' : 'Lịch tái khám người thân'}</h1>
                    </div>

                    {loading && <div className="revisit-alert">Đang tải thông tin...</div>}
                    {error && !loading && (
                        <div className="revisit-alert revisit-alert--warn">
                            {error}
                            {qp.time && <span> Hiển thị thông tin từ thông báo.</span>}
                        </div>
                    )}

                    <div className="revisit-info-card">
                        <InfoRow icon="fa-user" label="Bệnh nhân" value={patientName} />
                        <InfoRow icon="fa-tag" label="Mã hồ sơ" value={paramRecordId} />
                        <InfoRow icon="fa-clock" label="Giờ tái khám" value={revisitAt ? formatMedicalDateTime(revisitAt) : ''} highlight />
                        <InfoRow icon="fa-hospital" label="Bệnh viện / cơ sở" value={hospital} />
                        <InfoRow icon="fa-stethoscope" label="Chẩn đoán" value={diagnosis} />
                        <InfoRow icon="fa-notes-medical" label="Ghi chú bác sĩ" value={doctorNotes} />
                        <InfoRow icon="fa-calendar-day" label="Ngày khám trước" value={visitDate ? formatMedicalDate(visitDate) : ''} />
                        <InfoRow icon="fa-bell" label="Nhắc trước" value={formatRemindBefore(remindBefore)} />
                    </div>

                    <Link to={medicalPath} className="revisit-open-btn">
                        <i className="fa-solid fa-folder-open" /> Mở hồ sơ y tế
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default RevisitAppointmentPage;
